import type { Ribbon } from '../alerts';
import type { QuotaUse, YearQuotaUse } from './quotaUse';

// Quota d'un volet en abrégé : 2h/mois, 10j/an.
export function shortRate(qte: unknown, unite: string): string | null {
  const q = Number(qte) || 0;
  if (!q) return null;
  const [unit, per] = unite.split('_');
  return `${q}${unit === 'jours' ? 'j' : 'h'}/${per === 'an' ? 'an' : 'mois'}`;
}

export type QuotaKind = 'correctif' | 'evolutif';

/* Bandeau d'alerte d'un quota annuel sur la carte : dépassé (rouge) ou consommation plus rapide
   que l'échu à date (orange). « Consommation rapide » plutôt que « en avance », confondu avec
   l'avancement d'un projet (14/10/2026). */
export function quotaRibbon(
  name: string,
  kind: QuotaKind,
  use: YearQuotaUse | null,
): Ribbon | null {
  if (!use) return null;
  // Cochée « vue », l'alerte revient à la nouvelle année de contrat.
  const scope = use.year?.from || '';
  if (use.alert)
    return {
      kind,
      scope,
      level: 'alert',
      text: `${name} : quota de l'année dépassé, prévenir le client`,
    };
  if (use.ahead)
    return {
      kind,
      scope,
      level: 'warn',
      text: `${name} : consommation rapide, prévenir le client`,
    };
  return null;
}

/** Anneau de consommation (64 px, rayon 26) : longueur de l'arc et position du point « échu ». */
export const RING_R = 26;
export const RING_C = 2 * Math.PI * RING_R;

export function ringArc(use: Pick<QuotaUse, 'pct'>): number {
  return use.pct == null ? 0 : (Math.min(use.pct, 100) / 100) * RING_C;
}

// Point de repère sur l'anneau (quota échu, temps écoulé), dans le sens horaire depuis midi.
export function duePoint(
  use: Pick<YearQuotaUse, 'due' | 'quota'>,
  center = 32,
): { x: number; y: number } | null {
  if (use.due == null || !(use.quota > 0)) return null;
  const angle = Math.min(use.due / use.quota, 1) * 2 * Math.PI;
  const round = (n: number) => Math.round(n * 10) / 10;
  return {
    x: round(center + RING_R * Math.sin(angle)),
    y: round(center - RING_R * Math.cos(angle)),
  };
}
